import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  DatePicker,
  Label,
  Text,
  InfoIcon,
  Input,
  Select,
  TruckIcon,
  Tooltip,
} from '@mercai/clever'
import { ModalDeliveryProof } from '@/components/cards-order-center-show/card-order-statuses/components'

import { useTransportCompanyList, useVehiclesList } from '@/services'
import {
  LogisticShipmentProps,
  LogisticShipmentStatusEnum,
  LogisticShipmentTransportOptionsProps,
  SaleOrderProps,
} from '@/types'
import type { LogisticFormTransportProps } from './transport.type'

import styles from './transport.module.css'

export const LogisticFormTransport = ({
  onChange,
  values = {} as SaleOrderProps,
}: LogisticFormTransportProps) => {
  const { t } = useTranslation('componentOrderCenterForm')
  const { t: tEnums } = useTranslation('enums')

  const [isOpenDeliveryProofWarningModal, setIsOpenDeliveryProofWarningModal] =
    useState(false)
  const { data: vehiclesData } = useVehiclesList({})

  const { data: transportCompaniesData } = useTransportCompanyList({
    params: {
      page: '1',
      limit: '99',
      search: '',
    },
  })

  const onChangeShipmentValues = (
    keyParam: keyof LogisticShipmentProps,
    value?: string,
    forceUpdate?: boolean,
  ) => {
    const hasProofOfDelivery = values?.shipments.every(
      (shipment) => shipment.proofOfDelivery !== null,
    )

    if (
      value === LogisticShipmentStatusEnum.delivered &&
      !hasProofOfDelivery &&
      !forceUpdate
    ) {
      setIsOpenDeliveryProofWarningModal(true)
      return
    }

    const shipment = { ...values?.shipments?.[0] }
    const updatedShipment = { ...shipment, [keyParam]: value }

    onChange({
      ...values,
      shipments: [updatedShipment],
    })
  }

  const onChangeTransportValues = (
    keyParam: keyof LogisticShipmentTransportOptionsProps,
    value?: string,
  ) => {
    const shipment = { ...values?.shipments?.[0] }
    const updatedTransportOptions = {
      ...shipment.transportOptions,
      [keyParam]: value,
    }

    const shipments = values?.shipments?.map((s) =>
      s.uuid === shipment.uuid
        ? { ...s, transportOptions: updatedTransportOptions }
        : s,
    )

    onChange({
      ...values,
      shipments: shipments as LogisticShipmentProps[],
    })
  }

  const vehicles =
    vehiclesData?.data?.results?.map((vehicle) => ({
      label: vehicle.name,
      value: vehicle.slug!,
    })) || []

  const transportCompanies =
    transportCompaniesData?.data?.results?.map((company) => ({
      label: company.name.toLocaleUpperCase(),
      value: company.name.toLocaleUpperCase(),
    })) || []

  return (
    <>
      <div className={styles.title}>
        <TruckIcon />
        <Text variant="title-base">{t('logisticTitle')}</Text>
      </div>

      <div className={styles.content}>
        <div className={styles.field}>
          <Label>{t('logisticStatusLabel')}</Label>
          <Select
            options={[
              {
                label: tEnums('logistic_shipment_status_pending'),
                value: 'pending',
              },
              {
                label: tEnums('logistic_shipment_status_delivery_scheduled'),
                value: 'delivery_scheduled',
              },
              {
                label: tEnums('logistic_shipment_status_in_transit'),
                value: 'in_transit',
              },
              {
                label: tEnums('logistic_shipment_status_partially_delivered'),
                value: 'partially_delivered',
              },
              {
                label: tEnums('logistic_shipment_status_delivered'),
                value: 'delivered',
              },
              {
                label: tEnums('logistic_shipment_status_cancelled'),
                value: 'cancelled',
              },
              {
                label: tEnums('logistic_shipment_status_refused'),
                value: 'refused',
              },
              {
                label: tEnums('logistic_shipment_status_on_hold'),
                value: 'on_hold',
              },
            ]}
            value={values?.shipments?.[0]?.status}
            onChange={(newValue) => onChangeShipmentValues('status', newValue)}
          />
        </div>

        <div className={styles.field}>
          <Label>
            {t('logisticExpectedDeliveryDaysLabel')}

            <Tooltip
              text={t('logisticExpectedDeliveryDaysDescription')}
              widthText="16rem"
            >
              <InfoIcon className={styles['info-icon']} />
            </Tooltip>
          </Label>
          <Input
            value={`${values?.shipments?.[0]?.expectedDeliveryDays} ${t('logisticExpectedDeliveryDaysSuffix')}`}
            onChange={(newValue) =>
              onChangeShipmentValues('expectedDeliveryDays', newValue)
            }
            disabled
          />
        </div>

        <div className={styles.field}>
          <Label>
            {t('logisticExpectedDeliveryDateLabel')}

            <Tooltip
              text={t('logisticExpectedDeliveryDateDescription')}
              widthText="16rem"
            >
              <InfoIcon className={styles['info-icon']} />
            </Tooltip>
          </Label>
          <DatePicker
            value={values?.shipments?.[0]?.expectedDeliveryDate}
            onChange={(newValue) =>
              onChangeShipmentValues('expectedDeliveryDate', newValue)
            }
            disabled
          />
        </div>

        <div className={styles.field}>
          <Label>
            {t('logisticExpectedDeliveryAtLabel')}

            <Tooltip
              text={t('logisticExpectedDeliveryAtDescription')}
              widthText="16rem"
            >
              <InfoIcon className={styles['info-icon']} />
            </Tooltip>
          </Label>
          <DatePicker
            value={values?.shipments?.[0]?.expectedDeliveryAt}
            onChange={(newValue) =>
              onChangeShipmentValues('expectedDeliveryAt', newValue)
            }
          />
        </div>

        <div className={styles.field}>
          <Label>
            {t('logisticDeliveredAtLabel')}

            <Tooltip
              text={t('logisticDeliveredAtDescription')}
              widthText="16rem"
            >
              <InfoIcon className={styles['info-icon']} />
            </Tooltip>
          </Label>
          <DatePicker
            value={values?.shipments?.[0]?.deliveredAt}
            onChange={(newValue) =>
              onChangeShipmentValues('deliveredAt', newValue)
            }
          />
        </div>
      </div>

      <div className={styles.content}>
        <Text variant="text-large-bold">
          {values?.shipments?.[0]?.deliveryOption === 'pick_up'
            ? t('logisticTransportDataPickupTitle')
            : t('logisticTransportDataReceiveAtAddressTitle')}
        </Text>
        <div className={styles.field}>
          <Label>{t('logisticTransportConductorLabel')}</Label>
          <Input
            value={values?.shipments?.[0]?.transportOptions?.driver}
            onChange={(newValue) => onChangeTransportValues('driver', newValue)}
          />
        </div>
        <div className={styles.field}>
          <Label>{t('logisticTransportTransportCompanyLabel')}</Label>
          <Select
            options={transportCompanies}
            value={values?.shipments?.[0]?.transportOptions?.transportCompany?.toLocaleUpperCase()}
            onChange={(newValue) =>
              onChangeTransportValues('transportCompany', newValue)
            }
          />
        </div>
        <div className={styles.field}>
          <Label>{t('logisticTransportTruckTypeLabel')}</Label>
          <Select
            options={vehicles}
            value={values?.shipments?.[0]?.transportOptions?.vehicleType}
            onChange={(newValue) =>
              onChangeTransportValues('vehicleType', newValue)
            }
          />
        </div>
        <div className={styles.field}>
          <Label>{t('logisticTransportPlateLabel')}</Label>
          <Input
            value={values?.shipments?.[0]?.transportOptions?.vehicleIdentifier}
            onChange={(newValue) =>
              onChangeTransportValues('vehicleIdentifier', newValue)
            }
          />
        </div>

        <div className={styles.field}>
          <Label>{t('logisticTransportExternalTrackingReference')}</Label>
          <Input
            value={
              values?.shipments?.[0]?.transportOptions
                ?.externalTrackingReference
            }
            onChange={(newValue) =>
              onChangeTransportValues('externalTrackingReference', newValue)
            }
          />
        </div>

        {values?.shipments?.[0]?.deliveryOption === 'receive_at_address' && (
          <div className={styles.field}>
            <Label>{t('logisticTransportLinkLabel')}</Label>
            <Input
              value={values?.shipments?.[0]?.transportOptions?.trackingInfo}
              onChange={(newValue) =>
                onChangeTransportValues('trackingInfo', newValue)
              }
            />
          </div>
        )}
      </div>

      <ModalDeliveryProof
        saleOrderUuid={values?.uuid}
        shipment={values?.shipments?.[0]}
        isOpen={isOpenDeliveryProofWarningModal}
        onSaveAfterUpload={() => {
          onChangeShipmentValues(
            'status',
            LogisticShipmentStatusEnum.delivered,
            true,
          )
          setIsOpenDeliveryProofWarningModal(false)
        }}
        onClose={() => {
          setIsOpenDeliveryProofWarningModal(false)
        }}
      />
    </>
  )
}
